import {ReportSummaryQuestionTypeID} from '../../../core/entities/reportAnalytics/constants';
import type {parseReportSummary} from '../../../core/repositories/reportSummaryRepository';
import {useParseJson} from './parseJson';

type Result = Array<{
  typeID: number;
  typeName: string;
  summary: string;
}>;

const mergeSummaries = (
  result1: parseReportSummary,
  result2: parseReportSummary,
): parseReportSummary => {
  const data =
    !result1.summary || !result2.summary
      ? []
      : [
          ...(result1.summary.data as never[]),
          ...(result2.summary.data as never[]),
        ];
  const reference = result1.summary ? [...result1.summary.reference] : [];

  return {
    typeID: result1.typeID,
    typeName: result2.typeName,
    summary: {
      data,
      reference,
    },
    reference: result1.reference,
  };
};

export function parseSummaries(summaries: Result) {
  const {parseJson} = useParseJson();

  if (!summaries) {
    return {result: [], reference: []};
  }

  const result: parseReportSummary[] = summaries.map(
    (item): parseReportSummary => {
      if (!item.summary) {
        return {
          typeID: item.typeID,
          typeName: item.typeName,
          summary: undefined,
          reference: [],
        };
      }

      const parsedSummary = parseJson(item.summary);
      return {
        typeID: item.typeID,
        typeName: item.typeName,
        summary: parsedSummary ?? undefined,
        reference: parsedSummary.reference,
      };
    },
  );

  // 同じtypeIDを持つ場合、summaryをマージする
  const mergedResult: parseReportSummary[] = [];
  for (const item of result) {
    if (
      (item.typeID as ReportSummaryQuestionTypeID) ===
      ReportSummaryQuestionTypeID.QUESTION
    ) {
      mergedResult.push(item);
      continue;
    }

    const index = mergedResult.findIndex(
      (mergedItem) => mergedItem.typeID === item.typeID,
    );
    if (index === -1) {
      mergedResult.push(item);
    } else if (mergedResult[index] !== undefined) {
      mergedResult[index] = mergeSummaries(mergedResult[index], item);
    }
  }

  const reference: number[] = result
    .flatMap((item) => item.reference)
    .filter((value, index, self) => self.indexOf(value) === index);

  return {result, mergedResult, reference};
}
